import { useState } from "react";

/**
 * Shared required-field validation for the customer edit screen (Details +
 * Shipping sections) and the customer create wizard (Company step).
 *
 * All company/address fields are mandatory (except EORI, which is optional);
 * the shipping fields are mandatory only while "Shipping address is
 * different..." is checked.
 */

export interface RequiredFieldError {
  /** Form field name (matches the input's name attribute). */
  field: string;
  /** On-screen label, used in the summary line near the save button. */
  label: string;
  /** Per-field helper text shown under the input. */
  message: string;
}

export type FieldErrorMap = Partial<Record<string, string>>;

export const REQUIRED_FIELD_MESSAGE = "This field is required";
export const INVALID_EMAIL_MESSAGE = "Must be a valid email";

// Same rough pattern the approval dialogs use — not a security feature.
const EMAIL_PATTERN = /^\S+@\S+$/;

export function isValidEmail(value: string | undefined): boolean {
  return !!value && EMAIL_PATTERN.test(value.trim());
}

function validateField(
  field: string,
  label: string,
  value: string | undefined,
  isEmail?: boolean
): RequiredFieldError | undefined {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return { field, label, message: REQUIRED_FIELD_MESSAGE };
  if (isEmail && !EMAIL_PATTERN.test(trimmed)) {
    return { field, label, message: INVALID_EMAIL_MESSAGE };
  }
  return undefined;
}

export interface CompanyRequiredFields {
  name?: string;
  email?: string;
  phone?: string;
  cvr?: string;
  address?: string;
  postCode?: string;
  city?: string;
  country?: string;
}

type CompanyFieldName = keyof CompanyRequiredFields;

// Labels as the user sees them (InputLabel renders with CSS `capitalize`).
const COMPANY_FIELD_LABELS: Record<CompanyFieldName, string> = {
  name: "Company Name",
  email: "Company Email",
  phone: "Company Phone",
  cvr: "CVR",
  address: "Address",
  postCode: "Postcode",
  city: "City",
  country: "Country",
};

/** Field order as rendered on the edit screen's Details section. EORI is
 * rendered between CVR and Address but is optional, so it is not listed. */
export const UPDATE_COMPANY_FIELD_ORDER: CompanyFieldName[] = [
  "name",
  "email",
  "phone",
  "cvr",
  "address",
  "postCode",
  "city",
  "country",
];

/** Field order as rendered on the create wizard's Company step. EORI is
 * rendered after Company phone but is optional, so it is not listed. */
export const CREATE_COMPANY_FIELD_ORDER: CompanyFieldName[] = [
  "name",
  "cvr",
  "email",
  "phone",
  "address",
  "postCode",
  "city",
  "country",
];

export function getCompanyFieldErrors(
  form: CompanyRequiredFields,
  order: CompanyFieldName[]
): RequiredFieldError[] {
  return order
    .map((field) =>
      validateField(
        field,
        COMPANY_FIELD_LABELS[field],
        form[field],
        field === "email"
      )
    )
    .filter((x): x is RequiredFieldError => !!x);
}

export interface ShippingRequiredFields {
  shippingName?: string;
  shippingAddress?: string;
  shippingPostCode?: string;
  shippingCity?: string;
  shippingCountry?: string;
  shippingContactPerson?: string;
  shippingContactPhone?: string;
  shippingContactEmail?: string;
}

/** Field order as rendered in the Shipping section. */
const SHIPPING_FIELDS: {
  field: keyof ShippingRequiredFields;
  label: string;
  isEmail?: boolean;
}[] = [
  { field: "shippingName", label: "Shipping Name" },
  { field: "shippingAddress", label: "Shipping Address" },
  { field: "shippingPostCode", label: "Shipping Postcode" },
  { field: "shippingCity", label: "Shipping City" },
  { field: "shippingCountry", label: "Shipping Country" },
  { field: "shippingContactPerson", label: "Shipping Contact Person" },
  { field: "shippingContactPhone", label: "Shipping Contact Phone" },
  { field: "shippingContactEmail", label: "Shipping Contact Email", isEmail: true },
];

export function getShippingFieldErrors(
  form: ShippingRequiredFields,
  differs: boolean
): RequiredFieldError[] {
  if (!differs) return [];
  return SHIPPING_FIELDS.map((x) =>
    validateField(x.field, x.label, form[x.field], x.isEmail)
  ).filter((x): x is RequiredFieldError => !!x);
}

export function toFieldErrorMap(errors: RequiredFieldError[]): FieldErrorMap {
  const map: FieldErrorMap = {};
  for (const error of errors) map[error.field] = error.message;
  return map;
}

/** "Please fill in: Company email, Postcode, City" — labels in screen order. */
export function buildMissingFieldsMessage(
  errors: RequiredFieldError[]
): string | undefined {
  if (errors.length === 0) return undefined;
  return `Please fill in: ${errors.map((x) => x.label).join(", ")}`;
}

/**
 * Scrolls to the first invalid field. Inputs are found by their name
 * attribute; the country autocompletes are wrapped in a [data-field] anchor.
 */
export function scrollToFirstErrorField(errors: RequiredFieldError[]): void {
  const first = errors[0];
  if (!first) return;
  const el =
    (document.getElementsByName(first.field)[0] as HTMLElement | undefined) ??
    document.querySelector<HTMLElement>(`[data-field="${first.field}"]`);
  el?.scrollIntoView({ behavior: "smooth", block: "center" });
}

/**
 * Per-field error visibility: a field's error shows once the field has been
 * blurred (touched) or after a blocked save/next attempt (showAll).
 * Blocking itself only happens on save/next — never on blur.
 */
export function useVisibleFieldErrors(
  errors: FieldErrorMap | undefined,
  showAll: boolean | undefined
) {
  const [touched, setTouched] = useState<Set<string>>(new Set());

  function markTouched(field: string) {
    if (!field) return;
    setTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field)));
  }

  function handleBlur(event: { target: { name?: string } }) {
    markTouched(event.target.name ?? "");
  }

  function errorFor(field: string): string | undefined {
    if (!errors) return undefined;
    if (!showAll && !touched.has(field)) return undefined;
    return errors[field];
  }

  return { errorFor, markTouched, handleBlur };
}
